/**
 * Range Advantage on Board — bank-style, 30 boards.
 */

import { PlayingCard } from "@/features/chat/components/PlayingCard";
import { Brain } from "@/shared/components/icons";
import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import {
  RANGE_ADVANTAGE_QUESTIONS,
  type RangeAdvantageQuestion,
  type Side,
} from "@/features/quiz/lib/rangeAdvantageQuiz";

const SIDES: Side[] = ["BTN", "BB"];

export function QuizRangeAdvantage() {
  const {
    question,
    revealedAnswer,
    isCorrect,
    correctAnswer,
    stats,
    submit,
    next,
    bankPosition,
    bankSize,
    isComplete,
    restart,
  } = useQuizSession<RangeAdvantageQuestion, Side>({
    quizType: "range_advantage",
    bank: RANGE_ADVANTAGE_QUESTIONS,
    getCorrectAnswer: (q) => q.advantage,
    getQuestionPayload: (q) => ({ board: q.board, category: q.category }),
  });

  return (
    <QuizLayout
      Icon={Brain}
      title="Range Advantage"
      subtitle="Single-raised pot, BTN vs BB: who's ahead on this board?"
      description="Tests reading a flop's range-advantage owner in a single-raised BTN-vs-BB pot. High cards favor the raiser; low and connected boards favor the caller. Range advantage drives c-bet frequency: getting this wrong leads to firing into the wrong half of your hands."
      stats={stats}
      bankPosition={bankPosition}
      bankSize={bankSize}
      isComplete={isComplete}
      onRestart={restart}
    >
      <div className="flex flex-col items-center gap-3">
        <div className="text-[10px] uppercase tracking-wider text-text-muted">
          Flop
        </div>
        <div className="flex items-center gap-2">
          {question.board.map((c, i) => (
            <PlayingCard key={`${i}-${c}`} token={c} size="md" />
          ))}
        </div>
      </div>

      <p className="text-center text-text text-base">
        Who has the range advantage?
      </p>

      <div className="grid grid-cols-2 gap-3">
        {SIDES.map((side) => (
          <QuizOption
            key={side}
            value={side}
            revealedAnswer={revealedAnswer}
            correctAnswer={correctAnswer}
            onSelect={submit}
            size="lg"
          >
            <span className="text-xl tracking-wide">
              {side === "BTN" ? "BTN raiser" : "BB caller"}
            </span>
          </QuizOption>
        ))}
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              <strong>
                {correctAnswer === "BTN" ? "BTN raiser" : "BB caller"}
              </strong>{" "}
              has range advantage.
            </>
          }
          rationale={question.rationale}
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}
